import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import BookingForm from '../components/BookingForm';
import SeoHead from '../components/SeoHead';
import { VEHICLES, getVehicleDisplayName } from '../data/bookingConfig';
import { resolveExistingRoutePath } from '../data/hubLanding';
import '../styles/RouteLanding.css';
import '../styles/SinganallurTaxi.css';

const PAGE_PATH = '/singanallur-taxi';
const BOOKING_ID = 'singanallur-booking';
const PHONE_TEL = 'tel:+919629954544';
const WHATSAPP_HREF = 'https://wa.me/919629954544';
const OG_IMAGE = '/images/banner/easy-drop-taxi-hero.webp';

const SEO_TITLE = 'Singanallur Taxi Service, Coimbatore | Local & Outstation Cabs';
const SEO_DESCRIPTION =
  'Book a taxi from Singanallur, Coimbatore for local travel, airport transfers and outstation trips. Call or WhatsApp Easy Drop Taxi to check availability and get a fare estimate.';

const FLEET_DISPLAY = [
  {
    type: 'Sedan',
    seats: '4 + 1 Seats',
    available: 'Dzire • Etios • Amaze • Aura',
    image: '/images/vehicle/suzuki-dzire-1.webp',
  },
  {
    type: 'SUV',
    seats: '7 + 1 Seats',
    available: 'Innova • Ertiga • Carens • Xylo',
    image: '/images/innova-car.webp',
  },
  {
    type: 'MUV',
    seats: '7 + 1 Seats',
    available: 'Innova Crysta',
    image: '/images/innova-crysta.webp',
  },
];

const SOUTH_DESTINATIONS = [
  { label: 'Madurai', drop: 'Madurai', lookup: 'Madurai' },
  { label: 'Theni', drop: 'Theni', lookup: 'Theni' },
  { label: 'Dindigul', drop: 'Dindigul', lookup: 'Dindigul' },
  { label: 'Tirunelveli', drop: 'Tirunelveli', lookup: 'Thirunelveli' },
  { label: 'Tuticorin', drop: 'Tuticorin', lookup: 'Thoothukudi' },
  { label: 'Nagercoil', drop: 'Nagercoil', lookup: 'Nagarkovil' },
  { label: 'Kanyakumari', drop: 'Kanyakumari', lookup: 'kaniyakumari' },
  { label: 'Rameswaram', drop: 'Rameswaram', lookup: 'Rameshwaram' },
];

const FAQS = [
  {
    question: 'Can I book a taxi from Singanallur Bus Stand?',
    answer:
      'Yes. Customers can contact Easy Drop Taxi to enquire about taxi pickup from Singanallur and arrange a local or outstation trip, subject to availability.',
  },
  {
    question: 'Do you provide local taxi service in Singanallur?',
    answer:
      'Easy Drop Taxi provides local taxi services in and around Coimbatore. Contact the team to confirm pickup and destination availability.',
  },
  {
    question: 'Can I book a taxi from Singanallur to Madurai?',
    answer:
      'Yes. You can enquire about an outstation taxi from Singanallur/Coimbatore to Madurai and confirm the available vehicle and fare.',
  },
  {
    question: 'Can I book a taxi from Singanallur to Theni?',
    answer:
      'Yes. Contact Easy Drop Taxi for availability and fare details for a taxi from Singanallur/Coimbatore to Theni.',
  },
  {
    question: 'Can I book a taxi from Singanallur to Tirunelveli?',
    answer:
      'Yes. Outstation taxi enquiries from Singanallur/Coimbatore to Tirunelveli can be made through Easy Drop Taxi.',
  },
];

const scrollToBooking = () => {
  const target = document.getElementById(BOOKING_ID);
  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const SinganallurTaxi = () => {
  const [preferredVehicle, setPreferredVehicle] = useState('');
  const [selectedDrop, setSelectedDrop] = useState('');

  const destinations = useMemo(
    () => SOUTH_DESTINATIONS.map((item) => ({
      ...item,
      path: resolveExistingRoutePath('Coimbatore', item.lookup)
        || resolveExistingRoutePath('Coimbatore', item.drop),
    })),
    [],
  );

  const fleet = useMemo(
    () => FLEET_DISPLAY.map((card) => ({
      ...card,
      rate: VEHICLES.find((item) => item.type === card.type)?.rate,
    })),
    [],
  );

  const jsonLd = useMemo(() => [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://easydroptaxi.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Singanallur Taxi',
          item: `https://easydroptaxi.com${PAGE_PATH}`,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'TaxiService',
      name: 'Easy Drop Taxi',
      url: 'https://easydroptaxi.com/',
      telephone: '+919629954544',
      email: 'support@easydroptaxi.com',
      areaServed: ['Singanallur', 'Coimbatore', 'Tamil Nadu'],
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Coimbatore',
        addressRegion: 'Tamil Nadu',
        addressCountry: 'IN',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Singanallur Taxi Service',
      serviceType: 'Local and outstation taxi',
      url: `https://easydroptaxi.com${PAGE_PATH}`,
      provider: {
        '@type': 'TaxiService',
        name: 'Easy Drop Taxi',
        telephone: '+919629954544',
        url: 'https://easydroptaxi.com/',
      },
      areaServed: 'Singanallur, Coimbatore',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    },
  ], []);

  const handleSelectVehicle = (vehicleType) => {
    setPreferredVehicle(vehicleType);
    scrollToBooking();
  };

  const handleBookDrop = (drop) => {
    setSelectedDrop(drop);
    scrollToBooking();
  };

  return (
    <main className="route-landing singanallur-taxi">
      <SeoHead
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        path={PAGE_PATH}
        image={OG_IMAGE}
        jsonLd={jsonLd}
      />

      <section className="rl-hero">
        <div className="rl-hero-inner">
          <div>
            <nav className="rl-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span>Singanallur Taxi</span>
            </nav>
            <h1>Taxi Service in Singanallur, Coimbatore</h1>
            <p className="rl-hero-lead">
              Easy Drop Taxi provides local and outstation taxi services from Singanallur and nearby areas of Coimbatore. Book a cab for local travel, airport transfers and outstation journeys.
            </p>
            <div className="sg-hero-actions">
              <a className="sg-cta sg-cta-call" href={PHONE_TEL}>Call Now</a>
              <a
                className="sg-cta sg-cta-wa"
                href={WHATSAPP_HREF}
                target="_blank"
                rel="noopener noreferrer"
              >
                WhatsApp Now
              </a>
              <button type="button" className="sg-cta sg-cta-estimate" onClick={scrollToBooking}>
                Get Fare Estimate
              </button>
            </div>
          </div>

          <div id={BOOKING_ID} className="rl-booking-card">
            <BookingForm
              key={`${PAGE_PATH}-${selectedDrop}`}
              initialPickup="Singanallur, Coimbatore"
              initialDrop={selectedDrop}
              initialTripType="One Way"
              initialVehicle={preferredVehicle}
              searchButtonLabel="Get Fare Estimate"
            />
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Singanallur Local Taxi Service</h2>
          <div className="rl-prose">
            <p>
              If you need a local taxi in Singanallur, Easy Drop Taxi can pick you up for point-to-point travel within Coimbatore. That includes home-to-office trips, family visits, hospital runs and other everyday drops where a cab is more convenient than changing buses.
            </p>
            <p>
              Pickup can be arranged from a house, hotel or roadside point in the Singanallur area, then continue to another Coimbatore neighbourhood or to Coimbatore Airport when you need an airport transfer. Confirm the pickup point, destination and vehicle when you call, message on WhatsApp, or use the fare estimate form on this page.
            </p>
            <p>
              Easy Drop Taxi is based in Coimbatore and serves this part of the city as part of its regular coverage. There is no separate Singanallur branch listed here — booking still goes through the same phone, WhatsApp and online estimate flow used across the site.
            </p>
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Taxi from Singanallur Bus Stand</h2>
          <div className="rl-prose">
            <p>
              Travellers arriving at or leaving from Singanallur Bus Stand often want a direct cab instead of continuing by bus. A taxi from Singanallur Bus Stand can take you to a specific address in Coimbatore or start an outstation trip without another change of vehicle.
            </p>
            <p>
              A cab is a practical option when you are travelling with family, elderly passengers or heavy luggage, or when you prefer a door-to-door ride early in the morning or later in the evening. It is also useful when the next bus does not match your destination or timing.
            </p>
            <p>
              Availability depends on the time of travel and the vehicle you need. Call or WhatsApp Easy Drop Taxi with your pickup point near the bus stand and your drop, then confirm the fare through the existing estimate form.
            </p>
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Taxi from Singanallur to South Tamil Nadu</h2>
          <p className="rl-section-intro">
            Outstation taxis from Singanallur and the rest of Coimbatore are booked as one-way or round-trip trips. Fares follow the live mapped distance and the published per-kilometre tariff — they are not listed as fixed package prices on this page.
          </p>
          <div className="rl-prose">
            <p>
              Common southbound drops include Madurai, Theni, Dindigul, Tirunelveli, Tuticorin, Nagercoil, Kanyakumari and Rameswaram. Enter Singanallur or another Coimbatore pickup, add the destination, then use Get Fare Estimate for the current total.
            </p>
          </div>
          <div className="rl-route-grid sg-dest-grid">
            {destinations.map((item) => (
              <article key={item.label} className="rl-benefit-card">
                <h3>Singanallur → {item.label}</h3>
                <p>Outstation taxi enquiry from Singanallur / Coimbatore. Fare is shown after you estimate the trip.</p>
                <div className="rl-hub-route-actions">
                  {item.path ? (
                    <Link className="rl-select-btn" to={item.path}>
                      Open Coimbatore to {item.label} page
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className="rl-select-btn"
                      onClick={() => handleBookDrop(item.drop)}
                    >
                      Check fare from this page
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Taxi Service Around Singanallur</h2>
          <div className="rl-prose">
            <p>
              Singanallur sits on the Trichy Road side of Coimbatore, close to neighbourhoods such as Ramanathapuram in Coimbatore, Uppilipalayam, Peelamedu, Ondipudur and Hope College. Easy Drop Taxi can pick up from these areas when a mapped address is available — the same Coimbatore coverage used on the rest of the website.
            </p>
            <p>
              These names are nearby localities, not separate offices. If your pickup is a little further along Trichy Road or toward the airport, type the exact point in the form or share it on the call.
            </p>
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Taxi Services Available from Singanallur</h2>
          <p className="rl-section-intro">
            The same Easy Drop Taxi services used across Coimbatore apply here. Open a service page for more detail, or stay on this page to estimate a trip.
          </p>
          <div className="rl-links">
            <Link to="/services">Local taxi and other services</Link>
            <Link to="/services">Airport transfer</Link>
            <Link to="/services">Outstation taxi</Link>
            <Link to="/tariff">One way and round trip tariff</Link>
            <Link to="/taxi-routes">All taxi routes</Link>
            <Link to="/">Home</Link>
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Why Book Easy Drop Taxi from Singanallur</h2>
          <div className="rl-benefit-grid">
            <article className="rl-benefit-card">
              <h3>Phone and WhatsApp booking</h3>
              <p>Call +91 96299 54544 or message on WhatsApp to check a vehicle and confirm pickup from Singanallur.</p>
            </article>
            <article className="rl-benefit-card">
              <h3>Online fare estimate</h3>
              <p>Use the existing booking form on this page. Distance and fare follow the same live estimate used on other Easy Drop Taxi pages.</p>
            </article>
            <article className="rl-benefit-card">
              <h3>Air-conditioned vehicles</h3>
              <p>Sedan, SUV and Crysta options are available, subject to the vehicle on duty when you book.</p>
            </article>
            <article className="rl-benefit-card">
              <h3>Local and outstation</h3>
              <p>The same team handles Coimbatore local drops, airport transfers and south Tamil Nadu outstation trips.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Vehicles for a Singanallur Trip</h2>
          <p className="rl-section-intro">
            Rates below are the published per-kilometre tariff from Easy Drop Taxi’s existing pricing. The on-screen total still uses live distance and driver bata after you estimate.
          </p>
          <div className="rl-vehicle-grid">
            {fleet.map((vehicle) => (
              <article key={vehicle.type} className="rl-vehicle-card">
                <div className="rl-vehicle-media">
                  <img
                    src={vehicle.image}
                    alt={`${getVehicleDisplayName(vehicle)} taxi available with Easy Drop Taxi`}
                    width="480"
                    height="220"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="rl-vehicle-body">
                  <h3>{getVehicleDisplayName(vehicle)}</h3>
                  <p>Available: {vehicle.available}</p>
                  <div className="rl-vehicle-meta">
                    <span>{vehicle.seats}</span>
                    <span>₹{vehicle.rate}/km</span>
                  </div>
                  <button
                    type="button"
                    className={`rl-select-btn${preferredVehicle === vehicle.type ? ' is-selected' : ''}`}
                    onClick={() => handleSelectVehicle(vehicle.type)}
                  >
                    {preferredVehicle === vehicle.type ? 'Selected' : 'Use this category'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>How to Book a Taxi from Singanallur</h2>
          <div className="rl-step-grid">
            <article className="rl-step-card">
              <span className="rl-step-number">1</span>
              <h3>Enter pickup and destination</h3>
              <p>Keep Singanallur as pickup or type a closer address, then add your drop in Coimbatore or another city.</p>
            </article>
            <article className="rl-step-card">
              <span className="rl-step-number">2</span>
              <h3>Check the fare or contact us</h3>
              <p>Use Get Fare Estimate on this page, or call / WhatsApp Easy Drop Taxi if you want to confirm a vehicle first.</p>
            </article>
            <article className="rl-step-card">
              <span className="rl-step-number">3</span>
              <h3>Confirm the booking</h3>
              <p>Choose the vehicle category, share date and time, and complete the same booking steps used on the rest of the website.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="rl-faq">
            {FAQS.map((item) => (
              <details key={item.question}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Related pages</h2>
          <div className="rl-links">
            <Link to="/">Easy Drop Taxi home</Link>
            <Link to="/services">Taxi services in Coimbatore</Link>
            <Link to="/tariff">Tariff</Link>
            <Link to="/taxi-routes">Taxi routes</Link>
            <Link to="/coimbatore-to-madurai-taxi">Coimbatore to Madurai taxi</Link>
            <Link to="/contact">Contact</Link>
          </div>
        </div>
      </section>

      <section className="rl-cta">
        <div className="container">
          <h2>Need a cab from Singanallur?</h2>
          <p>
            Call Easy Drop Taxi, send a WhatsApp message, or estimate the fare on this page. Pickup stays in Singanallur / Coimbatore unless you change it.
          </p>
          <div className="sg-hero-actions sg-hero-actions-center">
            <a className="sg-cta sg-cta-call" href={PHONE_TEL}>Call Easy Drop Taxi</a>
            <a
              className="sg-cta sg-cta-wa"
              href={WHATSAPP_HREF}
              target="_blank"
              rel="noopener noreferrer"
            >
              WhatsApp Easy Drop Taxi
            </a>
            <button type="button" className="sg-cta sg-cta-estimate" onClick={scrollToBooking}>
              Get Fare Estimate
            </button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SinganallurTaxi;
